import AccreditationClient from "@equidefi/portals/clients/AccreditationClient";
import { useApi } from "@equidefi/portals/hooks/useApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * @typedef {{ file?: string, additional_info?: boolean, accreditation_method?: string }} AcceptAccreditationParams
 * @param {string} vaultId
 * @param {import("@tanstack/react-query").UseMutationOptions<unknown, unknown, AcceptAccreditationParams>?} options
 */
export const useAccept = (vaultId, options = {}) => {
  const accreditationApi = useApi(AccreditationClient);

  return useMutation(async (payload) => accreditationApi.accept(vaultId, payload), options);
};

/**
 * @typedef {{ file?: string, reason?: string }} RejectAccreditationParams
 * @param {string} vaultId
 * @param {string?} investmentId - Investment ID to invalidate notes cache
 * @param {import("@tanstack/react-query").UseMutationOptions<unknown, unknown, RejectAccreditationParams>?} options
 */
export const useReject = (vaultId, investmentId = null, options = {}) => {
  const accreditationApi = useApi(AccreditationClient);
  const queryClient = useQueryClient();

  return useMutation(async ({ file, reason }) => {
    const result = await accreditationApi.reject(vaultId, file, reason);

    // Invalidate notes cache when rejection is successful since a note is created on the backend
    if (investmentId) {
      queryClient.invalidateQueries(["investment", investmentId]);
    }

    return result;
  }, options);
};

/**
 * @param {string} vaultId
 * @param {import("@tanstack/react-query").UseMutationOptions?} options
 */
export const useManuallySubmit = (vaultId, options = {}) => {
  const accreditationApi = useApi(AccreditationClient);

  return useMutation(async () => accreditationApi.manuallySubmit(vaultId), options);
};
